import { useState } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { Lock, Globe, Pencil, Trash2, MoreVertical, Image as ImageIcon, Link as LinkIcon } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useLanguage } from '@/i18n/LanguageContext';
import type { TreslogEntry } from '@/types/treslog';

interface TreslogEntryCardProps {
  entry: TreslogEntry;
  onEdit: (entry: TreslogEntry) => void;
  onDelete: (id: string) => void;
  onTogglePrivacy: (id: string, isPublic: boolean) => void;
  onView: (entry: TreslogEntry) => void;
}

export function TreslogEntryCard({ entry, onEdit, onDelete, onTogglePrivacy, onView }: TreslogEntryCardProps) {
  const { language } = useLanguage();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const content = {
    en: {
      private: 'Private', public: 'Shared', edit: 'Edit', delete: 'Delete',
      makePublic: 'Share publicly', makePrivate: 'Make private', copyLink: 'Copy link',
      linkCopied: 'Link copied!', deleteTitle: 'Delete this entry?',
      deleteDescription: 'This action cannot be undone. This entry will be permanently deleted.',
      cancel: 'Cancel', confirmDelete: 'Delete',
    },
    es: {
      private: 'Privado', public: 'Compartido', edit: 'Editar', delete: 'Eliminar',
      makePublic: 'Compartir públicamente', makePrivate: 'Hacer privado', copyLink: 'Copiar enlace',
      linkCopied: '¡Enlace copiado!', deleteTitle: '¿Eliminar esta entrada?',
      deleteDescription: 'Esta acción no se puede deshacer. Esta entrada se eliminará permanentemente.',
      cancel: 'Cancelar', confirmDelete: 'Eliminar',
    },
    fr: {
      private: 'Privé', public: 'Partagé', edit: 'Modifier', delete: 'Supprimer',
      makePublic: 'Partager publiquement', makePrivate: 'Rendre privé', copyLink: 'Copier le lien',
      linkCopied: 'Lien copié !', deleteTitle: 'Supprimer cette entrée ?',
      deleteDescription: 'Cette action est irréversible. Cette entrée sera définitivement supprimée.',
      cancel: 'Annuler', confirmDelete: 'Supprimer',
    },
    pt: {
      private: 'Privado', public: 'Compartilhado', edit: 'Editar', delete: 'Excluir',
      makePublic: 'Compartilhar publicamente', makePrivate: 'Tornar privado', copyLink: 'Copiar link',
      linkCopied: 'Link copiado!', deleteTitle: 'Excluir esta entrada?',
      deleteDescription: 'Esta ação não pode ser desfeita. Esta entrada será permanentemente excluída.',
      cancel: 'Cancelar', confirmDelete: 'Excluir',
    },
    ar: {
      private: 'خاص', public: 'مشارك', edit: 'تعديل', delete: 'حذف',
      makePublic: 'مشاركة عامة', makePrivate: 'جعلها خاصة', copyLink: 'نسخ الرابط',
      linkCopied: 'تم نسخ الرابط!', deleteTitle: 'حذف هذا الإدخال؟',
      deleteDescription: 'لا يمكن التراجع عن هذا الإجراء. سيتم حذف هذا الإدخال نهائياً.',
      cancel: 'إلغاء', confirmDelete: 'حذف',
    },
    ja: {
      private: 'プライベート', public: '共有済み', edit: '編集', delete: '削除',
      makePublic: '公開で共有', makePrivate: '非公開にする', copyLink: 'リンクをコピー',
      linkCopied: 'リンクをコピーしました！', deleteTitle: 'このエントリーを削除しますか？',
      deleteDescription: 'この操作は取り消せません。このエントリーは完全に削除されます。',
      cancel: 'キャンセル', confirmDelete: '削除',
    },
    ko: {
      private: '비공개', public: '공유됨', edit: '편집', delete: '삭제',
      makePublic: '공개 공유', makePrivate: '비공개로 전환', copyLink: '링크 복사',
      linkCopied: '링크가 복사되었습니다!', deleteTitle: '이 항목을 삭제하시겠습니까?',
      deleteDescription: '이 작업은 취소할 수 없습니다. 이 항목은 영구적으로 삭제됩니다.',
      cancel: '취소', confirmDelete: '삭제',
    },
    id: {
      private: 'Pribadi', public: 'Dibagikan', edit: 'Edit', delete: 'Hapus',
      makePublic: 'Bagikan secara publik', makePrivate: 'Jadikan pribadi', copyLink: 'Salin tautan',
      linkCopied: 'Tautan disalin!', deleteTitle: 'Hapus entri ini?',
      deleteDescription: 'Tindakan ini tidak dapat dibatalkan. Entri ini akan dihapus secara permanen.',
      cancel: 'Batal', confirmDelete: 'Hapus',
    },
  };

  const handleCopyLink = () => {
    const url = `${window.location.origin}/treslog/${entry.id}`;
    navigator.clipboard.writeText(url);
    toast.success(t.linkCopied);
  };

  const t = content[language as keyof typeof content] || content.en;
  const photos = entry.photo_urls || [];

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        layout
      >
        <Card className="group hover:shadow-md transition-shadow duration-200 cursor-pointer" onClick={() => onView(entry)}>
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-lg text-foreground line-clamp-1 mb-1">
                  {entry.title}
                </h3>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>{format(new Date(entry.created_at), 'MMM d, yyyy')}</span>
                  <Badge variant={entry.is_public ? 'secondary' : 'outline'} className="gap-1">
                    {entry.is_public ? (
                      <>
                        <Globe className="h-3 w-3" />
                        {t.public}
                      </>
                    ) : (
                      <>
                        <Lock className="h-3 w-3" />
                        {t.private}
                      </>
                    )}
                  </Badge>
                  {photos.length > 0 && (
                    <span className="flex items-center gap-1 text-muted-foreground/70">
                      <ImageIcon className="h-3 w-3" />
                      {photos.length}
                    </span>
                  )}
                </div>
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEdit(entry)}>
                    <Pencil className="h-4 w-4 mr-2" />
                    {t.edit}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onTogglePrivacy(entry.id, !entry.is_public)}>
                    {entry.is_public ? (
                      <>
                        <Lock className="h-4 w-4 mr-2" />
                        {t.makePrivate}
                      </>
                    ) : (
                      <>
                        <Globe className="h-4 w-4 mr-2" />
                        {t.makePublic}
                      </>
                    )}
                  </DropdownMenuItem>
                  {entry.is_public && (
                    <DropdownMenuItem onClick={handleCopyLink}>
                      <LinkIcon className="h-4 w-4 mr-2" />
                      {t.copyLink}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => setShowDeleteDialog(true)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t.delete}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardHeader>

          <CardContent className="pt-0">
            {photos.length > 0 && (
              <div className="flex gap-2 mb-3 overflow-x-auto pb-2">
                {photos.slice(0, 3).map((url, idx) => (
                  <img
                    key={idx}
                    src={url}
                    alt=""
                    className="h-20 w-20 object-cover rounded-md flex-shrink-0"
                  />
                ))}
                {photos.length > 3 && (
                  <div className="h-20 w-20 bg-muted rounded-md flex items-center justify-center flex-shrink-0">
                    <span className="text-sm text-muted-foreground">+{photos.length - 3}</span>
                  </div>
                )}
              </div>
            )}
            <p className="text-muted-foreground line-clamp-3 text-sm leading-relaxed">
              {entry.content}
            </p>
          </CardContent>
        </Card>
      </motion.div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.deleteTitle}</AlertDialogTitle>
            <AlertDialogDescription>{t.deleteDescription}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t.cancel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => onDelete(entry.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t.confirmDelete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
